import template from "./report-details-modal.html";
import "./report-details-modal.less";
import {
	REPORT_SCOPES,
	EVENT_REMOVE_OPTIONS,
	EVENT_LOGOUT,
	EVENT_HOTKEY_LOCK,
	EVENT_PROCESS_UI_RULES
} from "constants.js";
import {
	EDIT_REPORT, NEW_REPORT
} from "routes.js";
import {
	find,
	forEach,
	groupBy,
	some,
	size,
	uniqBy
} from "lodash";
import { confirmUnsavedChanges } from "./prompts/confirmUnsavedChanges.js";
import hotkeyConfigs from "../../../services/hotkeyConfig";
import { TRIGGER_OPERATOR_CODES } from "../../../screens/rule-provider/constants";
import { EVENT_FILTER_BY_DETAILS } from "../../../constants";

class ReportDetailsModalController {
	constructor(
		$scope,
		auth,
		detailHelper,
		hotkeys,
		$logger,
		toastr,
		$timeout,
		$transitions
	) {
		this.detailValidationScopes = [REPORT_SCOPES.detail, REPORT_SCOPES.details];
		this.detailScope = [REPORT_SCOPES.detail];
		this.$scope = $scope;
		this.auth = auth;
		this.detailHelper = detailHelper;
		this.hotkeys = hotkeys;
		this.$logger = $logger;
		this.toastr = toastr;
		this.$timeout = $timeout;
		this.$transitions = $transitions;

		this.dirtyFields = {};
	}

	$onInit() {
		this.eventBus = this.resolve.reportScope.events;
		this.validationService = this.resolve.reportScope.validationService;
		this.detailId = this.resolve.detailId || 0;

		this.allDetailValues = !!this.resolve.allValues ? [...this.resolve.allValues] : [];
		this.uniqueFieldId = this.findSingleFieldUnique(this.resolve.rules);

		if(this.resolve.values) {
			this.validationService.loadRulesForDetailModal(this.resolve.detailId);

			this.handleDisplayValidations();
			this.modalInstance.rendered.then(() => {
				this.eventBus.trigger(EVENT_PROCESS_UI_RULES, { field: { IsDetail: true } });
				this.resolve.triggerFieldFilters(this.resolve.values);
				this.filterExistingDetailItems(this.uniqueFieldId);
			});
		}
		else {
			this.validationService.loadRulesForDetailModal();
			this.modalInstance.rendered.then(() => {
				this.eventBus.trigger(EVENT_PROCESS_UI_RULES, { field: { IsDetail: true } });
				this.resolve.initializeFields(this.resolve.form.detailFields);
				this.resolve.triggerFieldFilters([]);
				this.filterExistingDetailItems(this.uniqueFieldId);
			});
		}

		this.updateFieldErrors();
		this.setHotKeys();

		this.eventBus.global.register(EVENT_LOGOUT, () => { this.handleCancel(); }, this.$scope);

		this.eventBus.register(EVENT_FILTER_BY_DETAILS(this.uniqueFieldId), () => this.filterExistingDetailItems(this.uniqueFieldId), this.$scope);

		window.addEventListener("beforeunload", this.displayAlertIfDirty, false);

		window.addEventListener("keydown", this.handleKeydown, false);

		const deregisterTransitionHook = this.$transitions.onBefore(
			{
				from: state => {
					return state.name === NEW_REPORT || state.name === EDIT_REPORT;
				}
			},
			this.verifyNoUnwantedChanges
		);

		this.$scope.$on("$destroy", deregisterTransitionHook);

	}

	handleKeydown = async(e) => {
		if(e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
			e.preventDefault();

			const shouldContinueToCloseModal = await this.verifyNoUnwantedChanges();
			if(!shouldContinueToCloseModal) {
				return;
			}

			this.modalInstance.dismiss("escape");
		}
	};

	setHotKeys() {
		const hasLockableFields = some(this.resolve.form.detailFields, f => f.IsLockable);
		this.lockHotkeyExists = !!this.hotkeys.get(hotkeyConfigs.EDIT_REPORT.DETAILS.LOCK_FIELD().combo);

		if(hasLockableFields && !this.lockHotkeyExists) {
			this.hotkeys.bindTo(this.$scope).add(hotkeyConfigs.EDIT_REPORT.DETAILS.LOCK_FIELD(() => {
				this.eventBus.trigger(EVENT_HOTKEY_LOCK);
			}));
		}

		if(!this.resolve.form.readonly) {
			this.hotkeys.bindTo(this.$scope).add(hotkeyConfigs.EDIT_REPORT.DETAILS.SAVE_DETAIL(() => {
				this.handleSave(false);
			}));
		}

		if(!this.resolve.values && !this.resolve.form.readonly) {
			this.hotkeys.bindTo(this.$scope).add(hotkeyConfigs.EDIT_REPORT.DETAILS.SAVE_AND_ADD_DETAIL(() => {
				this.handleSave(true);
			}));
		}
	}

	isDirty = () => {
		return size(this.dirtyFields) > 0;
	};

	verifyNoUnwantedChanges = async() => {
		if(!this.isDirty()) {
			return true;
		}
		try {
			const confirmed = await confirmUnsavedChanges();

			return confirmed;
		}catch{
			return false;
		}
	};

	displayAlertIfDirty = (e) => {
		// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
		if(this.isDirty()) {
			// Cancel the event
			e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
			// Chrome requires returnValue to be set
			e.returnValue = "";
		}
	};

	handleSave(shouldHoldOpen) {
		this.doNotClose = shouldHoldOpen;

		let updates = this.getFormValues();

		if(updates === null) {
			this.doNotClose = false;
			throw new Error("save error");
		}

		const results = this.detailHelper.explodePivotedRowsAndAssignLinkKeys(updates, this.resolve.form.detailFields, this.repeatToKeyMap);
		this.repeatToKeyMap = results.repeatToLinkKeyMap;
		this.keyToIdMap = this.keyToIdMap || {};

		updates = results.updates;

		const detailsByKey = updates.length > 0 ?
			groupBy(updates, u => u.DetailLinkKey) :
			{ [this.repeatToKeyMap[-1]]: [] };

		const saveDetails = {};
		forEach(detailsByKey, (d, key) => {
			const newDetail = {
				detailId: this.resolve.detailId || this.keyToIdMap[key] || this.auth.uuid(),
				values: d,
				isDeleted: false,
				isNewDetail: !this.resolve.detailId || isNaN(this.resolve.detailId)
			};

			saveDetails[newDetail.detailId] = newDetail;
			this.keyToIdMap[key] = newDetail.detailId;
		});

		const returnPromise = this.resolve.onSave(saveDetails);

		if(!this.doNotClose) {
			return returnPromise.then((allResults) => {
				this.checkBlockSave(allResults)
					.then(() => {
						this.validationService.commitValidations();
						this.modalInstance.close(updates);
					});
			});
		}
		else {
			this.doNotClose = false;

			return returnPromise.then((allResults) => {
				this.checkBlockSave(allResults)
					.then(() => {
						this.repeatToKeyMap = {};
						this.keyToIdMap = {};
						this.detailId = 0;

						this.validationService.commitValidations();
						this.handleDisplayValidations();

						this.resolve.values = undefined;
						this.validationService.loadRulesForDetailModal();
						this.resolve.initializeFields(this.resolve.form.detailFields);

						this.eventBus.trigger(EVENT_PROCESS_UI_RULES, { isDetail: true });

						const lockedFieldIds = this.resolve.form.detailFields
							.filter(f => f.isLocked === true && f.Pivot === undefined)
							.map(f => f.Id);
						const lockedValues = updates.filter(v => lockedFieldIds.indexOf(v.FieldId) > -1);
						const uniqueLockedValues = uniqBy(lockedValues, v => v.FieldId);

						this.resolve.triggerFieldFilters(uniqueLockedValues);

						this.allDetailValues = this.allDetailValues.concat([...updates]);
						this.filterExistingDetailItems(this.uniqueFieldId);
						this.$timeout(() => this.$scope.$apply());
					});
			});
		}
	}

	checkBlockSave(allResults) {
		return new Promise((resolve) => {
			const hasBlockSave = some(allResults, r => r.blockSave === true);

			if(!hasBlockSave) {
				resolve();
			}
			else {
				const blockSaveRules = [];

				forEach(allResults, r => {
					this.validationService.updateTriggeredRules(r.triggeredRules, [REPORT_SCOPES.detail, REPORT_SCOPES.details], r.detailId);
					forEach(r.triggeredRules, tr => {
						const isBlockSaveRule = some(tr.Triggers, t => t.Operator.Code === TRIGGER_OPERATOR_CODES.blockSave);
						if(isBlockSaveRule) {
							blockSaveRules.push(tr);
						}
					});
				});
				this.handleDisplayValidations(true);
				this.$timeout(() => this.$scope.$apply());
			}
		});
	}

	filterExistingDetailItems(uniqueFieldId) {
		if(!!uniqueFieldId) {
			const currentDetailItems = this.allDetailValues.filter(val => val.FieldId === uniqueFieldId && val.IsDeleted !== true);
			const itemIdsToRemove = currentDetailItems.map(val => val.DataId);

			this.$scope.$$postDigest(() => {
				this.eventBus.trigger(EVENT_REMOVE_OPTIONS(uniqueFieldId), {
					items: itemIdsToRemove
				});
			});
		}
	}

	findSingleFieldUnique(rules) {
		if(!rules || rules.length === 0) {
			return null;
		}

		const uniqueDetailRule = find(rules, rule => {
			return rule.Engine && rule.Engine.Code === "UNIQUE" && ((rule.Scope && rule.Scope.Code === REPORT_SCOPES.details) || rule.Scope === undefined);
		});

		if(!!uniqueDetailRule) {
			if(uniqueDetailRule.Conditions.length === 1) {
				const uniqueFieldId = uniqueDetailRule.Conditions[0].Field.Id;
				return uniqueFieldId;
			}

			return null;
		}

		const childrenRules = rules.filter((rule) => {
			return rule.Scope && rule.Scope.Code === REPORT_SCOPES.details && !!rule.Children;
		});

		for(let i = 0; i < childrenRules.length; i++) {
			const uniqueChild = this.findSingleFieldUnique(childrenRules[i].Children);
			if(!!uniqueChild) {
				return uniqueChild;
			}
		}
		return null;
	}

	updateFieldErrors() {
		this.resolve.form.detailFields.map((field) => {
			field.hasValidationWarning = false;
			field.hasValidationError = false;
			forEach(this.validations, (type) => {
				forEach(type.rules, (rule) => {
					const ruleHasNoDetailId = rule.DetailId === 0 || rule.DetailId === undefined || isNaN(rule.DetailId);
					const resolveHasNoValues = this.resolve.values === undefined || this.resolve.values === null;
					const ruleScopeIsReportDetails = rule.Scope.Code === REPORT_SCOPES.details;
					const ruleDetailIdEqualsResolveDetailId = rule.DetailId === (this.resolve.values && this.resolve.detailId);

					const shouldSetValidation = (ruleHasNoDetailId && resolveHasNoValues)
						|| (ruleHasNoDetailId && ruleScopeIsReportDetails)
						|| ruleDetailIdEqualsResolveDetailId;

					let shouldCheckForWarningCode = shouldSetValidation && rule.appliedFieldIds.indexOf(field.Id) > -1 && !field.hasValidationError;

					const hasHighlightTrigger = some(rule.Triggers, t => t.Operator.Code === "HIGHLIGHT");

					if(hasHighlightTrigger) {
						const highlightTriggers = rule.Triggers.filter(t => t.Operator.Code === "HIGHLIGHT");
						shouldCheckForWarningCode = some(highlightTriggers, t => t.Field.Id === field.Id);
					}

					if(shouldCheckForWarningCode) {
						field.hasValidationError = type.code !== "WARNING";
						field.hasValidationWarning = type.code === "WARNING";
						return false;
					}
				});
			});
		});
	}

	getSaveVerb() {
		return this.resolve.values ? "Done" : "Add";
	}

	getButtonWidth() {
		return (this.resolve.values) ? "col-sm-6" : "col-sm-4";
	}

	handleDisplayValidations(blockSaveOnly = false) {
		const allValidations = [...this.validationService.pendingValidations];

		const detailValidations = allValidations
			.filter((type) => (type.code === TRIGGER_OPERATOR_CODES.blockSave) || blockSaveOnly === false)
			.map((type) => {
				const newType = { ...type };

				newType.rules = type.rules.filter(rule => {
					return (rule.DetailId === this.detailId)
						|| ((rule.DetailId === 0 || rule.DetailId === undefined || isNaN(rule.DetailId)) && this.resolve.values === undefined)
						|| ((rule.DetailId === 0 || rule.DetailId === undefined || isNaN(rule.DetailId)) && this.resolve.values === null)
						|| ((rule.DetailId === 0 || rule.DetailId === undefined || isNaN(rule.DetailId)) && rule.Scope.Code === "DETAILS");
				});

				return newType;
			});

		this.validations = detailValidations;

		this.updateFieldErrors();
	}

	async handleCancel() {
		const shouldContinueToCancel = await this.verifyNoUnwantedChanges();
		if(!shouldContinueToCancel) {
			return;
		}

		if(!!this.keyToIdMap && size(this.keyToIdMap) > 0) {
			forEach(this.keyToIdMap, id => {
				this.validationService.updateTriggeredRules([], [REPORT_SCOPES.detail], id);
			});
		}

		this.validationService.clearPendingValidations();

		this.modalInstance.dismiss("instance dismiss");
	}

	$onDestroy() {
		window.removeEventListener("beforeunload", this.displayAlertIfDirty);
		window.removeEventListener("keydown", this.handleKeydown);
	}
}

export default angular
	.module("vesl.components.report-details-modal", [])
	.component("reportDetailsModal", {
		controller: ReportDetailsModalController,
		template,
		bindings: {
			close: "&",
			dismiss: "&",
			modalInstance: "<",
			resolve: "<"
		}
	}).name;
